import { useEffect, useState } from 'react'
import { AlertCircle, Check, Loader2, Palette, Pencil, Plus, Trash2, X } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { EventTypeRow, EventTypeMutations } from '@/hooks/useEventTypes'

const DEFAULT_COLOR = '#2563eb'

/**
 * Add / edit / delete tenant event types. Each type has a label and a hex color
 * (the code is slugified server-side from the label and is immutable). System
 * defaults (`isSystem`) are editable but cannot be deleted — the backend returns
 * a 409, which is surfaced inline.
 */
export function ManageEventTypesModal({
  open,
  onClose,
  eventTypes,
  onAdd,
  onEdit,
  onRemove,
}: {
  open: boolean
  onClose: () => void
  eventTypes: EventTypeRow[]
  onAdd: EventTypeMutations['addEventType']
  onEdit: EventTypeMutations['renameOrRecolor']
  onRemove: EventTypeMutations['removeEventType']
}) {
  const [newLabel, setNewLabel] = useState('')
  const [newColor, setNewColor] = useState(DEFAULT_COLOR)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editColor, setEditColor] = useState(DEFAULT_COLOR)

  useEffect(() => {
    if (open) {
      setNewLabel('')
      setNewColor(DEFAULT_COLOR)
      setError(null)
      setBusy(false)
      setEditingId(null)
      setEditLabel('')
      setEditColor(DEFAULT_COLOR)
    }
  }, [open])

  const run = async (op: Promise<void>) => {
    setError(null)
    setBusy(true)
    try {
      await op
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleAdd = async () => {
    const label = newLabel.trim()
    if (!label || busy) return
    if (await run(onAdd({ label, color: newColor }))) {
      setNewLabel('')
      setNewColor(DEFAULT_COLOR)
    }
  }

  const startEdit = (type: EventTypeRow) => {
    setError(null)
    setEditingId(type.id)
    setEditLabel(type.label)
    setEditColor(type.color)
  }

  const handleEdit = async () => {
    if (editingId === null) return
    const label = editLabel.trim()
    if (!label || busy) return
    if (await run(onEdit(editingId, { label, color: editColor }))) setEditingId(null)
  }

  const handleRemove = async (id: string) => {
    if (busy) return
    await run(onRemove(id))
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <div className='flex items-start gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
            <Palette className='h-5 w-5' />
          </div>
          <div>
            <h2 className='text-base font-semibold text-gray-900'>Manage Event Types</h2>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              Add, recolor, or rename event types. Standard defaults can be edited but not deleted.
            </p>
          </div>
        </div>

        {error && (
          <div className='mt-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        <div className='mt-4 flex items-end gap-2'>
          <input
            type='color'
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            title='Pick a color'
            className='h-9 w-9 shrink-0 cursor-pointer rounded-md border border-gray-200 bg-white p-0.5'
          />
          <div className='flex-1 space-y-1'>
            <label className='text-xs font-medium text-gray-600'>New event type</label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder='e.g. Environmental Monitoring'
            />
          </div>
          <Button onClick={handleAdd} disabled={busy || newLabel.trim() === ''} className='gap-1.5'>
            {busy ? <Loader2 className='h-4 w-4 animate-spin' /> : <Plus className='h-4 w-4' />}
            Add
          </Button>
        </div>

        <div className='mt-4 max-h-[45vh] space-y-1.5 overflow-y-auto pr-1'>
          {eventTypes.length === 0 ? (
            <p className='py-6 text-center text-sm text-gray-400'>
              No event types yet. Add one above.
            </p>
          ) : (
            eventTypes.map((type) => (
              <div
                key={type.id}
                className='flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-2'
              >
                {editingId === type.id ? (
                  <>
                    <input
                      type='color'
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      title='Pick a color'
                      className='h-8 w-8 shrink-0 cursor-pointer rounded-md border border-gray-200 bg-white p-0.5'
                    />
                    <Input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleEdit()}
                      className='h-8 flex-1'
                      autoFocus
                    />
                    <button
                      type='button'
                      onClick={handleEdit}
                      disabled={busy || editLabel.trim() === ''}
                      title='Save'
                      className='text-emerald-600 transition-colors hover:text-emerald-700 disabled:opacity-40'
                    >
                      <Check className='h-4 w-4' />
                    </button>
                    <button
                      type='button'
                      onClick={() => setEditingId(null)}
                      title='Cancel'
                      className='text-gray-400 transition-colors hover:text-gray-600'
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </>
                ) : (
                  <>
                    <span
                      className='h-3.5 w-3.5 shrink-0 rounded-full'
                      style={{ backgroundColor: type.color }}
                    />
                    <span className='min-w-0 flex-1 truncate text-sm text-gray-800'>
                      {type.label}
                    </span>
                    {type.isSystem && (
                      <span className='shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500'>
                        system
                      </span>
                    )}
                    <button
                      type='button'
                      onClick={() => startEdit(type)}
                      disabled={busy}
                      title='Edit'
                      className='text-gray-400 transition-colors hover:text-blue-600 disabled:opacity-40'
                    >
                      <Pencil className='h-4 w-4' />
                    </button>
                    <button
                      type='button'
                      onClick={() => handleRemove(type.id)}
                      disabled={busy || type.isSystem}
                      title={type.isSystem ? 'System defaults cannot be deleted' : 'Delete'}
                      className='text-gray-400 transition-colors hover:text-destructive disabled:opacity-40'
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <DialogFooter className='mt-5'>
          <Button variant='outline' onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
